import { answersMatch, isAutoGradedQuestion } from './sampleMock';
import { isSessionTopic, SESSION_SECONDS, topicLabel, topicSlugFromName } from '../data/topics';

export const LOCAL_PROGRESS_KEY = 'cfa_progress_v1';

export function toDate(value) {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (typeof value.seconds === 'number') return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

const MAX_UNKNOWN_ATTEMPT_SECONDS = 6 * 60 * 60;

export function rawAttemptDurationSeconds(attempt) {
  if (!attempt) return 0;
  const hasStored = attempt.durationSeconds !== null &&
    attempt.durationSeconds !== undefined &&
    attempt.durationSeconds !== '';
  const stored = Number(attempt.durationSeconds);
  if (hasStored && Number.isFinite(stored)) return Math.max(0, Math.round(stored));
  const start = toDate(attempt.startTime);
  const end = toDate(attempt.endTime);
  if (!start || !end) return 0;
  return Math.max(0, Math.round((end.getTime() - start.getTime()) / 1000));
}

export function attemptDurationLimitSeconds(attempt) {
  if (attempt?.kind === 'session' || isSessionTopic(attempt?.topic, attempt?.level)) return SESSION_SECONDS;
  const count = Number(attempt?.questionCount) ||
    (Array.isArray(attempt?.questions) ? attempt.questions.length : 0) ||
    (Array.isArray(attempt?.questionSeconds) ? attempt.questionSeconds.length : 0);
  if (count > 0) return Math.max(15, count * 2) * 60;
  return MAX_UNKNOWN_ATTEMPT_SECONDS;
}

/** Match the quiz timer and reject multi-hour wall-clock/background-tab inflation. */
export function attemptDurationSeconds(attempt) {
  return Math.min(rawAttemptDurationSeconds(attempt), attemptDurationLimitSeconds(attempt));
}

export function attemptDurationWasCapped(attempt) {
  return rawAttemptDurationSeconds(attempt) > attemptDurationLimitSeconds(attempt);
}

/** Conservative fallback for legacy profiles when individual attempts cannot be loaded. */
export function normalizedProfileStudySeconds(profile = {}) {
  const stored = Math.max(0, Number(profile.studySeconds) || 0);
  const attempts = Math.max(0, Math.floor(Number(profile.attemptCount) || 0));
  if (attempts > 0) return Math.min(stored, attempts * SESSION_SECONDS);
  return Math.min(stored, MAX_UNKNOWN_ATTEMPT_SECONDS);
}

export function roundSeconds(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.round(n * 1000) / 1000;
}

export function formatDuration(totalSeconds) {
  const seconds = Math.max(0, Math.round(Number(totalSeconds) || 0));
  if (seconds < 60) return `${seconds}s`;
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (!hours) return `${minutes}m`;
  if (!minutes) return `${hours}h`;
  return `${hours}h ${minutes}m`;
}

export function formatClock(totalSeconds) {
  if (totalSeconds == null || !Number.isFinite(Number(totalSeconds))) return '—';
  const ms = Math.max(0, Math.round(Number(totalSeconds) * 1000));
  const hours = Math.floor(ms / 3600000);
  const minutes = Math.floor((ms % 3600000) / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  const millis = ms % 1000;
  const frac = `${seconds}.${String(millis).padStart(3, '0')}s`;
  if (hours) return minutes ? `${hours}h ${minutes}m ${frac}` : `${hours}h ${frac}`;
  if (minutes) return `${minutes}m ${frac}`;
  return frac;
}

export function topicBreakdown(questions = [], answers = []) {
  const grouped = {};
  questions.forEach((question, index) => {
    if (!isAutoGradedQuestion(question)) return;
    const key = question.topic || 'Other';
    if (!grouped[key]) grouped[key] = { correct: 0, total: 0 };
    grouped[key].total += 1;
    if (answersMatch(answers[index], question.correctAnswer)) grouped[key].correct += 1;
  });
  return grouped;
}

export function mergeTopicStats(existing = {}, incoming = {}) {
  const next = { ...existing };
  Object.entries(incoming).forEach(([key, stats]) => {
    const prev = next[key] || { correct: 0, total: 0 };
    next[key] = {
      correct: (prev.correct || 0) + (stats.correct || 0),
      total: (prev.total || 0) + (stats.total || 0),
    };
  });
  return next;
}

export function appendLocalAttempt(attempt) {
  try {
    const current = JSON.parse(localStorage.getItem(LOCAL_PROGRESS_KEY) || '{"attempts":[]}');
    const attempts = Array.isArray(current.attempts) ? current.attempts : [];
    attempts.unshift(attempt);
    localStorage.setItem(LOCAL_PROGRESS_KEY, JSON.stringify({ attempts: attempts.slice(0, 50) }));
  } catch (error) {
    console.error('Could not save local progress', error);
  }
}

export function loadLocalAttempts() {
  try {
    const current = JSON.parse(localStorage.getItem(LOCAL_PROGRESS_KEY) || '{"attempts":[]}');
    return Array.isArray(current.attempts) ? current.attempts : [];
  } catch {
    return [];
  }
}

/** Merge device-only attempts into cloud history without duplicates. */
export function mergeAttempts(cloud = [], local = []) {
  const keyOf = (row) =>
    `${row?.endTime || ''}|${row?.score || ''}|${row?.topic || ''}|${row?.questionCount || row?.questions?.length || ''}`;
  const seen = new Set((cloud || []).map(keyOf));
  const extra = (local || []).filter((row) => {
    const key = keyOf(row);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return [...(cloud || []), ...extra];
}

export function summarizeAttempts(attempts = []) {
  const ordered = [...attempts].sort(
    (a, b) => (toDate(b.endTime)?.getTime() || 0) - (toDate(a.endTime)?.getTime() || 0)
  );
  const sessions = ordered.filter((row) => isSessionTopic(row.topic, row.level));
  const latest = ordered[0] || null;
  const latestSession = sessions[0] || null;
  const previousSession = sessions[1] || null;
  const latestScore = latestSession ? Number(latestSession.score) : latest ? Number(latest.score) : null;
  const previousScore = previousSession ? Number(previousSession.score) : null;
  const trend =
    latestScore != null && previousScore != null && !Number.isNaN(latestScore) && !Number.isNaN(previousScore)
      ? Number((latestScore - previousScore).toFixed(1))
      : null;

  const topicsMap = {};
  ordered.forEach((attempt) => {
    const fromAttempt = attempt.topicStats || topicBreakdown(attempt.questions, attempt.answers);
    Object.entries(fromAttempt).forEach(([name, stats]) => {
      if (!topicsMap[name]) topicsMap[name] = { correct: 0, total: 0 };
      topicsMap[name].correct += stats.correct || 0;
      topicsMap[name].total += stats.total || 0;
    });
  });

  const topics = Object.entries(topicsMap)
    .map(([name, stats]) => {
      const pct = stats.total ? Math.round((stats.correct / stats.total) * 100) : 0;
      return {
        name,
        label: topicLabel(name),
        slug: topicSlugFromName(name),
        correct: stats.correct,
        total: stats.total,
        pct,
      };
    })
    .sort((a, b) => a.pct - b.pct);

  const ranked = [...topics].filter((row) => row.slug && row.total >= 3);
  const next = (ranked.length ? ranked : topics.filter((row) => row.slug))[0] || null;

  return {
    attemptCount: ordered.length,
    sessionCount: sessions.length,
    latest,
    latestSession,
    latestScore: latestScore == null || Number.isNaN(latestScore) ? null : latestScore,
    trend,
    topics,
    next,
  };
}
